import React, { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Textarea } from "@pgs-core/ui/components/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pgs-core/ui/components/select";
import { ImageField, type ImageFieldValue } from "./image-field";
import { createPostCategory, type PostAuthor, type PostCategory, type PostFaq } from "../lib/posts";
import type { ApiClient } from "../lib/api";

const NO_AUTHOR = "none";

/** The editable, non-content post fields as the settings rail holds them. */
export interface PostDraft {
  slug: string;
  excerpt: string;
  featuredImage: ImageFieldValue | null;
  authorUserId: string | null;
  categoryIds: string[];
  metaTitle: string;
  metaDesc: string;
  faqs: PostFaq[];
  published: 0 | 1;
  publishedAt: string;
}

/**
 * The post editor's settings rail: one stacked column beside the writing
 * surface — publish state, listing fields (slug/excerpt/featured image),
 * author, and categories. SEO fields live on the editor's SEO & AI tab.
 */
export function PostSettingsPanel({
  draft, onChange, categories, onCategoriesChanged, authors, api, slugError,
}: {
  draft: PostDraft;
  onChange: (patch: Partial<PostDraft>) => void;
  categories: PostCategory[];
  onCategoriesChanged: (categories: PostCategory[]) => void;
  authors: PostAuthor[];
  api: ApiClient;
  slugError: string;
}) {
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const toggleCategory = (id: string) => {
    onChange({
      categoryIds: draft.categoryIds.includes(id)
        ? draft.categoryIds.filter((existing) => existing !== id)
        : [...draft.categoryIds, id],
    });
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    setAddingCategory(true);
    setCategoryError("");
    try {
      const category = await createPostCategory(api, name);
      onCategoriesChanged([...categories, category].sort((a, b) => a.name.localeCompare(b.name)));
      onChange({ categoryIds: [...draft.categoryIds, category.id] });
      setNewCategory("");
    } catch (err) {
      setCategoryError((err as Error).message);
    } finally {
      setAddingCategory(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Visibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="inline-flex w-full rounded-md border p-0.5">
            {([
              { value: 0, label: "Draft" },
              { value: 1, label: "Published" },
            ] as const).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ published: option.value })}
                className={`flex-1 rounded px-2 py-1.5 text-sm transition-colors ${
                  draft.published === option.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-published-at">Publish date</Label>
            <Input
              id="post-published-at"
              type="date"
              value={draft.publishedAt}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ publishedAt: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Shown on the post and used for ordering. Set automatically the first time you publish.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Listing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="post-slug">URL slug</Label>
            <Input
              id="post-slug"
              value={draft.slug}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ slug: e.target.value })}
              aria-invalid={!!slugError}
            />
            {slugError ? (
              <p className="text-xs text-destructive">{slugError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Lives at /blog/{draft.slug}/</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-excerpt">Excerpt</Label>
            <Textarea
              id="post-excerpt"
              value={draft.excerpt}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange({ excerpt: e.target.value })}
              placeholder="A short summary shown on the blog page and in search results"
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Featured image</Label>
            <ImageField
              api={api}
              value={draft.featuredImage}
              onChange={(item) =>
                onChange({ featuredImage: item ? { key: item.key, url: item.url ?? null } : null })
              }
            />
            <p className="text-xs text-muted-foreground">
              Shown on the blog page. Without one, the first image in the post is used.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Author</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={draft.authorUserId ?? NO_AUTHOR}
            onValueChange={(value: string) => onChange({ authorUserId: value === NO_AUTHOR ? null : value })}
          >
            <SelectTrigger aria-label="Author">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_AUTHOR}>No author</SelectItem>
              {authors.map((author) => (
                <SelectItem key={author.user_id} value={author.user_id}>{author.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {categories.length === 0 && (
            <p className="text-xs text-muted-foreground">No categories yet — add one below.</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {categories.map((category) => {
              const selected = draft.categoryIds.includes(category.id);
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  aria-pressed={selected}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    selected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={newCategory}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCategory(e.target.value)}
              placeholder="New category"
              className="h-8"
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") void addCategory(); }}
            />
            <Button size="sm" variant="outline" disabled={addingCategory || !newCategory.trim()} onClick={() => { void addCategory(); }}>
              {addingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>
          {categoryError && <p className="text-xs text-destructive">{categoryError}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
